import { TreeNode } from 'primeng/api';

import {
  COLUMN_DELIMITER,
  CompanyModel,
  DEFAULT_DATE_FORMAT,
  EventActionItem,
  GridEventActionType,
  MemberPermissions,
  SiteModel,
} from '@customer-portal/shared';
import { convertUtcToLocalDateString } from '@customer-portal/shared/helpers/date/date.helpers';

import {
  City,
  Country,
  SettingsAdminListDto,
  SettingsAdminListItemDto,
  SettingsMembersListDto,
  SettingsMembersListItemDto,
} from '../../dtos';
import {
  isDnvUserHasRestrictedRole,
  shouldShowMemberEventActions,
} from '../../helpers';
import {
  AccessAreaResponse,
  CreateContactCompany,
  MEMBER_PERMISSION_ROLES,
  MemberAreaPermissions,
  MemberAreasPermissions,
  MemberPermissionRole,
  SettingsMembersItemModel,
  SettingsMembersPermissionsDataModel,
} from '../../models';

const MILLISECONDS_IN_24_HOURS = 24 * 60 * 60 * 1000;

const parseIsoToUtcMilliseconds = (
  isoDateTime?: string | null,
): number | null => {
  if (!isoDateTime) {
    return null;
  }

  const parsedDate = new Date(isoDateTime);

  if (Number.isNaN(parsedDate.getTime())) {
    return null;
  }

  return Date.parse(parsedDate.toISOString());
};

const extractUniqueSiteNames = (countries: Country[]): string[] => {
  const siteNamesSet = new Set<string>();

  countries.forEach((country) => {
    country.cities.forEach((city) => {
      city.sites.forEach((site) => {
        siteNamesSet.add(site.siteName);
      });
    });
  });

  return Array.from(siteNamesSet);
};

const extractUniqueCompanyNames = (companies: CompanyModel[]) =>
  Array.from(new Set(companies.map((company) => company.companyName)));

const isNewUser = (lastLoginTime: string | null) =>
  !lastLoginTime || lastLoginTime === '';

const createNode = (
  data: string | number,
  label: string,
  key: string,
  checked = false,
  children: TreeNode[] = [],
): TreeNode => ({
  data,
  label,
  key,
  children,
  checked,
});

const mapSitesToNodes = (
  sites: SiteModel[],
  nicknameBySiteId: Map<number, string>,
): TreeNode[] =>
  sites.map((site) => {
    const nickname =
      typeof site.siteId === 'number'
        ? nicknameBySiteId.get(site.siteId)
        : undefined;
    const siteNode = createNode(
      site.siteId!,
      nickname ? `${site.siteName} | ${nickname}` : site.siteName,
      `${site.siteId!}-${site.siteName}`,
      site.isSiteSelected,
    );

    return siteNode;
  });

const mapCitiesToNodes = (
  cities: City[],
  nicknameBySiteId: Map<number, string>,
): TreeNode[] =>
  cities.map((city) => {
    const cityNode = createNode(
      city.cityName,
      city.cityName,
      `${city.cityName}-city`,
      city.isCitySelected,
    );
    cityNode.children = mapSitesToNodes(city.sites, nicknameBySiteId);

    return cityNode;
  });

const mapCountriesToNodes = (
  countries: Country[],
  nicknameBySiteId: Map<number, string>,
): TreeNode[] =>
  countries.map((country) => {
    const countryNode = createNode(
      country.countryId,
      country.countryName,
      `${country.countryId}-${country.countryName}`,
      country.isCountrySelected,
    );
    countryNode.children = mapCitiesToNodes(country.cities, nicknameBySiteId);

    return countryNode;
  });

export class SettingsMembersMapper {
  static mapToMembersList(
    dto: SettingsMembersListDto,
    accountDnvId: string | null,
    cobrowsingUserEmail?: string | null,
    accessType?: string | null,
  ): SettingsMembersItemModel[] {
    if (!dto?.data) {
      return [];
    }

    const { data } = dto;
    const isDnvUserHasRestrictedRoleAccess =
      isDnvUserHasRestrictedRole(accessType ?? '') && !cobrowsingUserEmail;

    return data.map((member: SettingsMembersListItemDto) => {
      const services = Array.from(
        new Set(
          member.services.map((auditService) => auditService.serviceName),
        ),
      ).join(COLUMN_DELIMITER);

      const companies = extractUniqueCompanyNames(member.companies).join(
        COLUMN_DELIMITER,
      );

      const sites = extractUniqueSiteNames(member.countries).join(
        COLUMN_DELIMITER,
      );

      const {
        name,
        email,
        lastLoginTime,
        lastRetriggeredAt,
        isUserOperational,
        isAccountOperational,
        roles: access,
        canDelete,
        canManagePermissions,
      } = member;

      return {
        name,
        email,
        company: companies,
        services,
        sites,
        access,
        lastLoginTime: convertUtcToLocalDateString(
          lastLoginTime,
          DEFAULT_DATE_FORMAT,
        ),
        iconTooltip: {
          displayIcon: isNewUser(lastLoginTime),
          iconClass: 'pi pi-info-circle',
          iconPosition: 'suffix',
          tooltipMessage: 'settings.membersTab.userNotLoggedIn',
          tooltipParams: { portalName: 'My Certification portal' },
        },
        isDisabled: isNewUser(lastLoginTime),
        eventActions: shouldShowMemberEventActions(
          isDnvUserHasRestrictedRoleAccess,
          canManagePermissions,
          canDelete,
        )
          ? {
              id: email,
              actions: SettingsMembersMapper.getActions(
                accountDnvId,
                canManagePermissions,
                canDelete,
                cobrowsingUserEmail,
                isDnvUserHasRestrictedRoleAccess,
                lastRetriggeredAt,
                isUserOperational,
                isAccountOperational,
              ),
            }
          : undefined,
      };
    });
  }

  static mapToAdminList(
    dto: SettingsAdminListDto,
    accountDnvId: string | null,
    cobrowsingUserEmail?: string | null,
    accessType?: string | null,
  ): SettingsMembersItemModel[] {
    if (!dto?.data) {
      return [];
    }

    const { data } = dto;

    const isDnvUserHasRestrictedRoleAccess =
      isDnvUserHasRestrictedRole(accessType ?? '') && !cobrowsingUserEmail;

    return data.map((admin: SettingsAdminListItemDto) => {
      const company = extractUniqueCompanyNames(admin.companies).join(
        COLUMN_DELIMITER,
      );

      const {
        name,
        email,
        lastLoginTime,
        lastRetriggeredAt,
        isUserOperational,
        isAccountOperational,
        canDelete,
        canManagePermissions,
      } = admin;

      return {
        name,
        email,
        company,
        services: 'All Services',
        sites: 'All Sites',
        access: 'Admin',
        lastLoginTime: convertUtcToLocalDateString(
          lastLoginTime,
          DEFAULT_DATE_FORMAT,
        ),
        isDisabled: isNewUser(lastLoginTime),
        iconTooltip: {
          displayIcon: isNewUser(lastLoginTime),
          iconClass: 'pi pi-info-circle',
          iconPosition: 'suffix',
          tooltipMessage: 'settings.membersTab.userNotLoggedIn',
          tooltipParams: { portalName: 'My Certification portal' },
        },
        eventActions: shouldShowMemberEventActions(
          isDnvUserHasRestrictedRoleAccess,
          canManagePermissions,
          canDelete,
        )
          ? {
              id: email,
              name,
              actions: SettingsMembersMapper.getActions(
                accountDnvId,
                canManagePermissions,
                canDelete,
                cobrowsingUserEmail,
                isDnvUserHasRestrictedRoleAccess,
                lastRetriggeredAt,
                isUserOperational,
                isAccountOperational,
              ),
            }
          : undefined,
      };
    });
  }

  static mapToMemberCompanies(
    data: SettingsMembersPermissionsDataModel,
  ): TreeNode[] {
    const companies = data.data.flatMap((d) =>
      d.companies.map((company) => ({
        data: company.companyId,
        label: company.companyName,
        key: `${company.companyId}-${company.companyName}`,
        hasParentId: company.hasParentId,
        parentId: company.parentId,
        checked: company.isCompanySelected,
        children: [] as TreeNode[],
      })),
    );

    const parentCompanies: typeof companies = [];
    const childCompanies: typeof companies = [];

    companies.forEach((company) => {
      if (company.hasParentId) {
        childCompanies.push(company);
      } else {
        parentCompanies.push(company);
      }
    });

    childCompanies.forEach((child) => {
      const parentIdx = parentCompanies.findIndex(
        (parent) => child.parentId === parent.data,
      );

      if (parentIdx !== -1) {
        parentCompanies[parentIdx].children.push(child);
      } else {
        parentCompanies.push(child);
      }
    });

    return parentCompanies;
  }

  static mapToMemberServices(
    data: SettingsMembersPermissionsDataModel,
    selectedCompanyIds: number[],
  ): TreeNode[] {
    const services: TreeNode[] = [];

    data.data.forEach((d) => {
      d.companies.forEach((company) => {
        company.services.forEach((service) => {
          if (
            selectedCompanyIds.includes(company.companyId) &&
            !services.some((s) => s.data === service.serviceId)
          ) {
            services.push({
              data: service.serviceId,
              label: service.serviceName,
              key: `${service.serviceId}-${service.serviceName}`,
              checked: service.isServiceSelected,
              children: [],
            });
          }
        });
      });
    });

    return services;
  }

  static mapToMemberSites(
    data: SettingsMembersPermissionsDataModel,
    selectedServiceIds: number[],
    selectedCompanyIds: number[],
    nicknameBySiteId: Map<number, string> = new Map(),
  ): TreeNode[] {
    const countryMap = new Map<string | number, Country>();
    const countries = data.data.flatMap((d) =>
      d.companies
        .filter((company) => selectedCompanyIds.includes(company.companyId))
        .flatMap((company) =>
          company.services.flatMap((service) =>
            selectedServiceIds.includes(service.serviceId)
              ? service.countries
              : [],
          ),
        ),
    );

    countries.forEach((country) => {
      if (!countryMap.has(country.countryName)) {
        countryMap.set(country.countryName, { ...country, cities: [] });
      }
      const existingCountry = countryMap.get(country.countryName)!;
      country.cities.forEach((city) => {
        const existingCity = existingCountry.cities.find(
          (c) => c.cityName === city.cityName,
        );

        if (existingCity) {
          existingCity.sites = [...existingCity.sites, ...city.sites].filter(
            (site, index, self) =>
              index === self.findIndex((s) => s.siteId === site.siteId),
          );
        } else {
          existingCountry.cities.push({
            ...city,
            sites: [...city.sites],
          });
        }
      });
    });

    const mergedCountries = Array.from(countryMap.values());

    return mapCountriesToNodes(mergedCountries, nicknameBySiteId);
  }

  static mapToSelectedCompanies = (
    data: SettingsMembersPermissionsDataModel,
    selectedCompanyIds: number[],
  ): CreateContactCompany[] =>
    data.data
      .flatMap((d) => d.companies)
      .filter((company) => selectedCompanyIds.includes(company.companyId))
      .map((company) => ({
        companyId: company.companyId,
        companyName: company.companyName,
      }));

  static mapToSelectedServices = (
    data: SettingsMembersPermissionsDataModel,
    selectedServiceIds: number[],
  ): { serviceId: number; serviceName: string }[] => {
    const uniqueServicesMap = new Map<
      number,
      { serviceId: number; serviceName: string }
    >();

    data.data
      .flatMap((d) => d.companies)
      .flatMap((company) => company.services)
      .forEach((service) => {
        if (
          selectedServiceIds.includes(service.serviceId) &&
          !uniqueServicesMap.has(service.serviceId)
        ) {
          uniqueServicesMap.set(service.serviceId, {
            serviceId: service.serviceId,
            serviceName: service.serviceName,
          });
        }
      });

    return Array.from(uniqueServicesMap.values());
  };

  static mapToSelectedSites = (
    data: SettingsMembersPermissionsDataModel,
    selectedSiteIds: (string | number)[],
  ): any[] => {
    const countryMap = new Map<
      number,
      { countryId: number; countryName: string; cities: any[] }
    >();

    data.data.forEach((user) => {
      user.companies.forEach((company) => {
        company.services.forEach((service) => {
          service.countries.forEach((country) => {
            if (!countryMap.has(country.countryId)) {
              countryMap.set(country.countryId, {
                countryId: country.countryId,
                countryName: country.countryName,
                cities: [],
              });
            }
            const countryEntry = countryMap.get(country.countryId)!;
            country.cities.forEach((city) => {
              let cityEntry = countryEntry.cities.find(
                (c) => c.cityName === city.cityName,
              );

              if (!cityEntry) {
                cityEntry = { cityName: city.cityName, sites: [] };
                countryEntry.cities.push(cityEntry);
              }

              const existingSiteIds = new Set(
                cityEntry.sites.map((s: any) => s.siteId),
              );

              city.sites.forEach((site) => {
                if (
                  selectedSiteIds.includes(site.siteId ?? '') &&
                  !existingSiteIds.has(site.siteId)
                ) {
                  cityEntry.sites.push({
                    siteId: site.siteId,
                    siteName: site.siteName,
                  });
                  existingSiteIds.add(site.siteId);
                }
              });
            });
          });
        });
      });
    });

    return Array.from(countryMap.values())
      .map((country) => ({
        ...country,
        cities: country.cities.filter((city) => city.sites.length > 0),
      }))
      .filter((country) => country.cities.length > 0);
  };

  static mapAccessAreasToMemberPermissions(
    accessAreas: AccessAreaResponse[],
  ): MemberAreasPermissions[] {
    const accessAreaSet = new Set(accessAreas.map((a) => a.roleName));

    const rolesByArea = MEMBER_PERMISSION_ROLES.reduce<
      Record<MemberAreaPermissions, MemberPermissionRole[]>
    >(
      (acc, role) => {
        acc[role.area] = acc[role.area] || [];
        acc[role.area].push(role);

        return acc;
      },
      {} as Record<MemberAreaPermissions, MemberPermissionRole[]>,
    );

    const allAreas: MemberAreaPermissions[] = [
      MemberAreaPermissions.Contracts,
      MemberAreaPermissions.Schedules,
      MemberAreaPermissions.Audits,
      MemberAreaPermissions.Findings,
      MemberAreaPermissions.Certificates,
      MemberAreaPermissions.Financials,
    ];

    const allTypes: (keyof MemberPermissions)[] = ['view', 'edit', 'submit'];

    return allAreas.map((area) => {
      const permission: MemberPermissions = {};
      const areaRoles = rolesByArea[area] || [];

      allTypes.forEach((type) => {
        const role = areaRoles.find((r) => r.type === type);

        if (role) {
          permission[type] = accessAreaSet.has(role.roleName)
            ? { isChecked: true, roleId: role.roleId, roleName: role.roleName }
            : {
                isChecked: false,
                roleId: role.roleId,
                roleName: role.roleName,
              };
        } else {
          permission[type] = undefined;
        }
      });

      return { area, permission };
    });
  }

  static getActions(
    accountDnvId: string | null,
    canManagePermissions: boolean,
    canDelete: boolean,
    cobrowsingUserEmail?: string | null,
    isDnvUserHasRestrictedRoleAccess?: boolean,
    lastRetriggeredAt?: string | null,
    isUserOperational?: boolean,
    isAccountOperational?: boolean,
  ): EventActionItem[] {
    const isCobrowsingView = !!cobrowsingUserEmail;
    const lastRetriggeredAtMs = parseIsoToUtcMilliseconds(lastRetriggeredAt);
    const isWelcomeEmailCooldownActive =
      lastRetriggeredAtMs !== null &&
      Date.now() - lastRetriggeredAtMs < MILLISECONDS_IN_24_HOURS;
    const isWelcomeEmailDisabled =
      isUserOperational !== true ||
      isAccountOperational !== true ||
      isWelcomeEmailCooldownActive;

    const disableRemove =
      isCobrowsingView || !canDelete || !!isDnvUserHasRestrictedRoleAccess;

    return [
      ...(accountDnvId && !cobrowsingUserEmail
        ? [
            {
              label: GridEventActionType.ViewPortalAs,
              i18nKey: `gridEvent.${GridEventActionType.ViewPortalAs}`,
              icon: 'pi pi-eye',
              disabled: !!cobrowsingUserEmail,
            },
          ]
        : []),
      {
        label: GridEventActionType.ManagePermissions,
        i18nKey: `gridEvent.${GridEventActionType.ManagePermissions}`,
        icon: 'pi pi-lock',
        disabled: !canManagePermissions || !!isDnvUserHasRestrictedRoleAccess,
      },
      ...(!isDnvUserHasRestrictedRoleAccess && accountDnvId
        ? [
            {
              label: GridEventActionType.SendWelcomeEmail,
              i18nKey: `gridEvent.${GridEventActionType.SendWelcomeEmail}`,
              icon: 'pi pi-envelope',
              disabled: isWelcomeEmailDisabled,
              disabledTooltipLabel: isWelcomeEmailCooldownActive
                ? 'settings.membersTab.sendWelcomeEmailDisabledTooltip'
                : undefined,
            },
          ]
        : []),
      {
        label: GridEventActionType.Remove,
        i18nKey: `gridEvent.${GridEventActionType.Remove}`,
        icon: 'pi pi-trash',
        disabled: !!disableRemove,
      },
    ];
  }
}
